// Lifecycle ribbon card on the Contact cockpit. Wraps the generic
// LifecycleRibbon UI primitive with the cockpit-specific transition
// matrix (lifecycleTransitions.ts). The parent owns commit/confirm/
// invalid-toast logic — this component only emits the picked stage id
// and a flag indicating whether it's reachable.

import React from 'react';

import type { LifecycleStage } from '../../../types/contact';
import { LIFECYCLE_STAGE_LABELS } from '../../../types/contact';
import { Card, LifecycleRibbon } from '../../../ui';
import {
  COCKPIT_RIBBON_STAGES,
  getReachableTargets,
  isValidTransition,
} from './lifecycleTransitions';

export interface ContactLifecycleRibbonProps {
  stage: LifecycleStage;
  /** Fires when the operator clicks any segment (valid or invalid). */
  onSelect: (target: LifecycleStage, isValid: boolean) => void;
}

const ContactLifecycleRibbon: React.FC<ContactLifecycleRibbonProps> = ({
  stage,
  onSelect,
}) => {
  const reachable = getReachableTargets(stage);
  return (
    <Card padding="tight" className="mb-4">
      <div className="flex items-end justify-between mb-2.5 gap-2">
        <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">
          Lifecycle · click a stage to transition
        </span>
        <span className="text-[11px] text-slate-500">
          currently{' '}
          <code className="text-slate-300 font-mono">{stage}</code>
        </span>
      </div>
      <LifecycleRibbon
        stages={COCKPIT_RIBBON_STAGES}
        currentStageId={stage}
        reachableStageIds={reachable}
        onSelect={(picked) => {
          const pickedStage = picked as LifecycleStage;
          if (pickedStage === stage) return;
          onSelect(pickedStage, isValidTransition(stage, pickedStage));
        }}
        notReachableHint={(_picked, currentLabel) =>
          `Not a valid transition from ${currentLabel}`
        }
      />
      <div className="mt-2 text-[11px] text-slate-500">
        Reachable from{' '}
        <code className="text-slate-300 font-mono">
          {LIFECYCLE_STAGE_LABELS[stage]}
        </code>
        :{' '}
        {reachable.length === 0 ? (
          <span className="text-slate-500">terminal · no transitions</span>
        ) : (
          reachable.map((r, i) => (
            <React.Fragment key={r}>
              {i > 0 ? ', ' : ''}
              <code className="text-slate-300 font-mono">{r}</code>
            </React.Fragment>
          ))
        )}
      </div>
    </Card>
  );
};

export default ContactLifecycleRibbon;
